// SolarInstallationProjectGallery.js - Solar installation themed Project Gallery section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a solar installation themed Project Gallery section with categorized projects
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the gallery section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The project gallery section elements
 */
export const createSolarInstallationProjectGalleryElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const gallerySectionClass = 'solar-gallery-section';
  const galleryContainerClass = 'solar-gallery-container';
  const galleryHeaderClass = 'solar-gallery-header';
  const galleryTitleClass = 'solar-gallery-title';
  const gallerySubtitleClass = 'solar-gallery-subtitle';
  const galleryTabsClass = 'solar-gallery-tabs';
  const galleryTabClass = 'solar-gallery-tab';
  const galleryTabActiveClass = 'solar-gallery-tab-active';
  const galleryContentClass = 'solar-gallery-content';
  const galleryGridClass = 'solar-gallery-grid';
  const galleryItemClass = 'solar-gallery-item';
  const galleryImageClass = 'solar-gallery-image';
  const galleryOverlayClass = 'solar-gallery-overlay';
  const galleryInfoClass = 'solar-gallery-info';
  const galleryProjectTitleClass = 'solar-gallery-project-title';
  const galleryProjectDescClass = 'solar-gallery-project-desc';
  const galleryProjectStatsClass = 'solar-gallery-project-stats';
  const galleryStatClass = 'solar-gallery-stat';

  // Desktop Styles - Solar themed with bluish background
  const gallerySectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#0f2341',
    background: `
      radial-gradient(circle at 30% 20%, rgba(74, 222, 128, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 70% 80%, rgba(34, 197, 94, 0.06) 0%, transparent 50%),
      linear-gradient(135deg, #0f2341 0%, #1e3a8a 25%, #1d4ed8 50%, #1e3a8a 75%, #0f2341 100%)
    `,
    color: '#f8fafc',
    width: '100%',
    overflow: 'hidden'
  };

  const galleryContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
    zIndex: '1'
  };

  const galleryHeaderStyles = {
    textAlign: 'center',
    marginBottom: '60px'
  };

  const galleryTitleStyles = {
    fontSize: '48px',
    fontWeight: '700',
    color: '#f8fafc',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif',
    letterSpacing: '-0.02em',
    lineHeight: '1.2'
  };

  const gallerySubtitleStyles = {
    fontSize: '20px',
    color: '#cbd5e1',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  const galleryTabsStyles = {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '50px',
    flexWrap: 'wrap'
  };

  const galleryTabStyles = {
    padding: '14px 24px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#cbd5e1',
    backgroundColor: 'rgba(15, 35, 65, 0.6)',
    border: '1px solid rgba(74, 222, 128, 0.2)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    fontFamily: '"Inter", sans-serif'
  };

  const galleryTabActiveStyles = {
    color: '#0f2341',
    backgroundColor: '#4ade80',
    borderColor: '#4ade80',
    boxShadow: '0 4px 10px rgba(74, 222, 128, 0.3)'
  };

  const galleryContentStyles = {
    width: '100%'
  };

  const galleryGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '30px',
    marginTop: '30px'
  };

  const galleryItemStyles = {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: 'rgba(30, 58, 138, 0.3)',
    border: '1px solid rgba(74, 222, 128, 0.2)',
    transition: 'all 0.3s ease',
    cursor: 'pointer'
  };

  const galleryItemHoverStyles = {
    transform: 'translateY(-4px)',
    borderColor: '#4ade80',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.1), 0 0 20px rgba(74, 222, 128, 0.2)'
  };

  const galleryImageStyles = {
    width: '100%',
    height: '250px',
    objectFit: 'cover',
    objectPosition: 'center',
    transition: 'transform 0.3s ease'
  };

  const galleryOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(to bottom, transparent 0%, rgba(15, 35, 65, 0.8) 70%, rgba(15, 35, 65, 0.95) 100%)',
    display: 'flex',
    alignItems: 'flex-end',
    padding: '30px',
    opacity: '0',
    transition: 'opacity 0.3s ease'
  };

  const galleryOverlayHoverStyles = {
    opacity: '1'
  };

  const galleryInfoStyles = {
    padding: '25px',
    backgroundColor: 'rgba(15, 35, 65, 0.9)',
    backdropFilter: 'blur(10px)'
  };

  const galleryProjectTitleStyles = {
    fontSize: '20px',
    fontWeight: '600',
    color: '#f8fafc',
    marginBottom: '8px',
    fontFamily: '"Inter", sans-serif',
    lineHeight: '1.3'
  };

  const galleryProjectDescStyles = {
    fontSize: '14px',
    color: '#cbd5e1',
    lineHeight: '1.5',
    marginBottom: '15px',
    fontFamily: '"Inter", sans-serif'
  };

  const galleryProjectStatsStyles = {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap'
  };

  const galleryStatStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  };

  // Tablet and Mobile styles
  const tabletGalleryGridStyles = {
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '25px'
  };

  const mobileGalleryTitleStyles = {
    fontSize: '36px'
  };

  const mobileGalleryTabsStyles = {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px'
  };

  const mobileGalleryGridStyles = {
    gridTemplateColumns: '1fr',
    gap: '20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(gallerySectionClass, gallerySectionStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryContainerClass, galleryContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryHeaderClass, galleryHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryTitleClass, galleryTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(gallerySubtitleClass, gallerySubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryTabsClass, galleryTabsStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryTabClass, galleryTabStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryTabActiveClass, galleryTabActiveStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryContentClass, galleryContentStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryGridClass, galleryGridStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryItemClass, galleryItemStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageClass, galleryImageStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryOverlayClass, galleryOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryInfoClass, galleryInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryProjectTitleClass, galleryProjectTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryProjectDescClass, galleryProjectDescStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryProjectStatsClass, galleryProjectStatsStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryStatClass, galleryStatStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${galleryItemClass}--hover`, galleryItemHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryOverlayClass}--hover`, galleryOverlayHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(galleryGridClass, tabletGalleryGridStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(galleryTitleClass, mobileGalleryTitleStyles);
    newRegistry[DEVICES.MOBILE].set(galleryTabsClass, mobileGalleryTabsStyles);
    newRegistry[DEVICES.MOBILE].set(galleryGridClass, mobileGalleryGridStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    gallerySectionClass,
    galleryContainerClass,
    galleryHeaderClass,
    galleryTitleClass,
    gallerySubtitleClass,
    galleryTabsClass,
    galleryTabClass,
    galleryTabActiveClass,
    galleryContentClass,
    galleryGridClass,
    galleryItemClass,
    galleryImageClass,
    galleryOverlayClass,
    galleryInfoClass,
    galleryProjectTitleClass,
    galleryProjectDescClass,
    galleryProjectStatsClass,
    galleryStatClass
  ]));

  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = Date.now() + 1;
  const headerId = Date.now() + 2;
  const titleId = Date.now() + 3;
  const subtitleId = Date.now() + 4;
  const tabsId = Date.now() + 5;
  const tab1Id = Date.now() + 6; // Residential
  const tab2Id = Date.now() + 7; // Commercial
  const tab3Id = Date.now() + 8; // Industrial
  const contentId = Date.now() + 9;
  const gridId = Date.now() + 10;

  // Project IDs (6 projects total - 2 per category)
  const project1Id = Date.now() + 20;
  const project2Id = Date.now() + 30;
  const project3Id = Date.now() + 40;
  const project4Id = Date.now() + 50;
  const project5Id = Date.now() + 60;
  const project6Id = Date.now() + 70;

  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Project Gallery Section',
      parentId: null,
      className: gallerySectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'gallery-section',
        'data-component': 'section',
        'data-section-type': 'project-gallery',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Gallery Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: galleryContainerClass,
      styles: {},
      children: [headerId, tabsId, contentId],
      text: '',
      attributes: {
        'data-element': 'gallery-container'
      }
    },
    
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Gallery Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: galleryHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'gallery-header'
      }
    },
    
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Gallery Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: galleryTitleClass,
      styles: {},
      children: [],
      text: 'Our Project Gallery',
      attributes: {
        'data-element': 'gallery-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Gallery Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: gallerySubtitleClass,
      styles: {},
      children: [],
      text: 'Explore our completed solar installation projects across residential, commercial, and industrial sectors. Each project showcases our commitment to quality and sustainable energy solutions.',
      attributes: {
        'data-element': 'gallery-subtitle'
      }
    },
    
    // Content
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Gallery Content',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: galleryContentClass,
      styles: {},
      children: [gridId],
      text: '',
      attributes: {
        'data-element': 'gallery-content'
      }
    },
    
    // Grid
    {
      id: gridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Gallery Grid',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: galleryGridClass,
      styles: {},
      children: [project1Id, project2Id, project3Id, project4Id, project5Id, project6Id],
      text: '',
      attributes: {
        'data-element': 'gallery-grid'
      }
    }
  ];

  // Add project items (simplified structure for brevity)
  const projects = [
    {
      id: project1Id,
      title: 'Modern Family Home',
      desc: '15kW residential solar system with battery storage',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      category: 'residential',
      stats: ['15kW', '24 Panels', '85% Savings']
    },
    {
      id: project2Id,
      title: 'Suburban Villa',
      desc: '20kW rooftop installation with smart monitoring',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      category: 'residential',
      stats: ['20kW', '32 Panels', '90% Savings']
    },
    {
      id: project3Id,
      title: 'Office Complex',
      desc: '100kW commercial solar installation',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      category: 'commercial',
      stats: ['100kW', '250 Panels', '70% Savings']
    },
    {
      id: project4Id,
      title: 'Shopping Center',
      desc: '250kW large-scale commercial project',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      category: 'commercial',
      stats: ['250kW', '625 Panels', '65% Savings']
    },
    {
      id: project5Id,
      title: 'Manufacturing Plant',
      desc: '500kW industrial solar farm installation',
      image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      category: 'industrial',
      stats: ['500kW', '1250 Panels', '60% Savings']
    },
    {
      id: project6Id,
      title: 'Warehouse Facility',
      desc: '750kW large-scale industrial installation',
      image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      category: 'industrial',
      stats: ['750kW', '1875 Panels', '55% Savings']
    }
  ];

  // Add project elements
  projects.forEach((project, index) => {
    const imageId = project.id + 1;
    const overlayId = project.id + 2;
    const infoId = project.id + 3;
    const titleId = project.id + 4;
    const descId = project.id + 5;
    const statsId = project.id + 6;

    newElements.push(
      // Project Item
      {
        id: project.id,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Project ${index + 1}`,
        parentId: gridId,
        hierarchyName: getHierarchyName('div'),
        className: galleryItemClass,
        styles: {},
        children: [imageId, overlayId, infoId],
        text: '',
        attributes: {
          'data-element': 'gallery-item',
          'data-category': project.category
        }
      },
      
      // Project Image
      {
        id: imageId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'image',
        name: `Project ${index + 1} Image`,
        parentId: project.id,
        hierarchyName: getHierarchyName('image'),
        className: galleryImageClass,
        styles: {},
        src: project.image,
        children: [],
        attributes: {
          'data-element': 'gallery-image',
          'alt': project.title
        }
      },
      
      // Project Overlay
      {
        id: overlayId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Project ${index + 1} Overlay`,
        parentId: project.id,
        hierarchyName: getHierarchyName('div'),
        className: galleryOverlayClass,
        styles: {},
        children: [],
        text: '',
        attributes: {
          'data-element': 'gallery-overlay'
        }
      },
      
      // Project Info
      {
        id: infoId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Project ${index + 1} Info`,
        parentId: project.id,
        hierarchyName: getHierarchyName('div'),
        className: galleryInfoClass,
        styles: {},
        children: [titleId, descId, statsId],
        text: '',
        attributes: {
          'data-element': 'gallery-info'
        }
      },
      
      // Project Title
      {
        id: titleId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'h3',
        name: `Project ${index + 1} Title`,
        parentId: infoId,
        hierarchyName: getHierarchyName('h3'),
        className: galleryProjectTitleClass,
        styles: {},
        children: [],
        text: project.title,
        attributes: {
          'data-element': 'gallery-project-title'
        }
      },
      
      // Project Description
      {
        id: descId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'p',
        name: `Project ${index + 1} Description`,
        parentId: infoId,
        hierarchyName: getHierarchyName('p'),
        className: galleryProjectDescClass,
        styles: {},
        children: [],
        text: project.desc,
        attributes: {
          'data-element': 'gallery-project-desc'
        }
      },
      
      // Project Stats
      {
        id: statsId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Project ${index + 1} Stats`,
        parentId: infoId,
        hierarchyName: getHierarchyName('div'),
        className: galleryProjectStatsClass,
        styles: {},
        children: [],
        text: project.stats.join(' • '),
        attributes: {
          'data-element': 'gallery-project-stats'
        }
      }
    );
  });

  return {
    gallerySection: newElements[0],
    elements: newElements
  };
} 